<template>
  <div
    class="bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-300 border-slate-50 dark:border-slate-800/50 border-l rtl:border-l-0 rtl:border-r contact--panel overflow-y-auto"
  >
    <contact-info
      :contact="contact"
      :channel-type="channelType"
      @toggle-panel="onPanelToggle"
      :close-icon-name="isSmartphone ? 'dismiss' : 'chevron-right'"
    />
    <draggable
      :list="conversationSidebarItems"
      :disabled="!dragEnabled || isSmartphone"
      animation="200"
      class="list-group"
      ghost-class="ghost"
      handle=".drag-handle"
      @start="dragging = true"
      @end="onDragEnd"
    >
      <transition-group>
        <div
          v-for="element in conversationSidebarItems"
          :key="element.name"
          class="bg-white dark:bg-gray-800"
        >
          <div
            v-if="element.name === 'conversation_actions'"
            class="conversation--actions"
          >
            <accordion-item
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')"
              :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
              @click="
                value => toggleSidebarUIState('is_conv_actions_open', value)
              "
            >
              <conversation-action
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </accordion-item>
          </div>
          <div
            v-else-if="element.name === 'conversation_participants'"
            class="conversation--actions"
          >
            <accordion-item
              :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
              :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
              @click="
                value =>
                  toggleSidebarUIState('is_conv_participants_open', value)
              "
            >
              <conversation-participant
                :conversation-id="conversationId"
                :inbox-id="inboxId"
              />
            </accordion-item>
          </div>
          <div v-else-if="element.name === 'contact_form'">
            <accordion-item
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_FORM')"
              :is-open="isContactSidebarItemOpen('is_contact_form_open')"
              compact
              @click="
                value => toggleSidebarUIState('is_contact_form_open', value)
              "
            >
              <template #button>
                <woot-button
                  v-if="!isSmartphone"
                  variant="link"
                  size="small"
                  @click.stop.prevent="openContactFormModal"
                >
                  Destacar
                </woot-button>
              </template>
              <contact-form
                :contact="contact"
                :in-progress="contactsUiFlags.isUpdating"
                :on-submit="onSubmitContactInfo"
                @cancel="
                  () => toggleSidebarUIState('is_contact_form_open', false)
                "
              />
            </accordion-item>
          </div>
          <div v-else-if="element.name === 'conversation_info'">
            <accordion-item
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
              :is-open="isContactSidebarItemOpen('is_conv_details_open')"
              compact
              @click="
                value => toggleSidebarUIState('is_conv_details_open', value)
              "
            >
              <conversation-info
                :conversation-attributes="conversationAdditionalAttributes"
                :contact-attributes="contactAdditionalAttributes"
              />
            </accordion-item>
          </div>
          <div v-else-if="element.name === 'contact_attributes'">
            <accordion-item
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
              :is-open="isContactSidebarItemOpen('is_contact_attributes_open')"
              compact
              @click="
                value =>
                  toggleSidebarUIState('is_contact_attributes_open', value)
              "
            >
              <custom-attributes
                attribute-type="contact_attribute"
                attribute-class="conversation--attribute"
                class="even"
                :contact-id="contact.id"
              />
              <custom-attribute-selector
                attribute-type="contact_attribute"
                :contact-id="contact.id"
              />
            </accordion-item>
          </div>
          <div v-else-if="element.name === 'previous_conversation'">
            <accordion-item
              v-if="contact.id"
              :title="
                $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
              "
              :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
              @click="
                value => toggleSidebarUIState('is_previous_conv_open', value)
              "
            >
              <contact-conversations
                :contact-id="contact.id"
                :conversation-id="conversationId"
              />
            </accordion-item>
          </div>
          <woot-feature-toggle
            v-else-if="element.name === 'macros'"
            feature-key="macros"
          >
            <accordion-item
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
              :is-open="isContactSidebarItemOpen('is_macro_open')"
              compact
              @click="value => toggleSidebarUIState('is_macro_open', value)"
            >
              <macros-list :conversation-id="conversationId" />
            </accordion-item>
          </woot-feature-toggle>
          <div v-else-if="element.name === 'kedu_contracts'">
            <accordion-item
              v-if="contact.id"
              :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTRACTS')"
              :is-open="isContactSidebarItemOpen('is_kedu_contract_open')"
              @click="
                value => toggleSidebarUIState('is_kedu_contract_open', value)
              "
            >
              <school-contract-panel
                :contact-id="contact.id"
                :conversation-id="conversationId"
              />
            </accordion-item>
          </div>
        </div>
      </transition-group>
    </draggable>
    <woot-draggable-modal
      :show.sync="showContactFormModal"
      :on-close="closeContactFormModal"
      :closeOnBackdropClick="false"
    >
      <template #header>
        <woot-modal-header
          :header-title="
            `${$t('EDIT_CONTACT.TITLE')} - ${contactDisplayName}`
          "
          :header-content="$t('EDIT_CONTACT.DESC')"
        >
          <woot-button variant="link" size="small" @click="closeContactFormModal">
            Voltar ao painel
          </woot-button>
        </woot-modal-header>
      </template>
      <contact-form
        :contact="contact"
        :in-progress="contactsUiFlags.isUpdating"
        :on-submit="onSubmitContactInfo"
        @success="onContactFormSuccess"
        @cancel="closeContactFormModal"
      />
    </woot-draggable-modal>
  </div>
</template>

<script>
import AccordionItem from 'dashboard/components/Accordion/AccordionItem';
import alertMixin from 'shared/mixins/alertMixin';
import deviceMixins from 'shared/mixins/deviceMixins';

import { mapGetters } from 'vuex';
import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';

import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import draggable from 'vuedraggable';
import ConversationInfo from './ConversationInfo';
import MacrosList from './Macros/List';
import ContactInfo from './contact/ContactInfo';
import ContactForm from './contact/Form.vue';
import SchoolContractPanel from './contact/schoolForm/SchoolContractPanel.vue';
import CustomAttributeSelector from './customAttributes/CustomAttributeSelector.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';

export default {
  components: {
    AccordionItem,
    ContactConversations,
    ContactInfo,
    ContactForm,
    ConversationInfo,
    CustomAttributes,
    CustomAttributeSelector,
    ConversationAction,
    ConversationParticipant,
    draggable,
    MacrosList,
    SchoolContractPanel,
  },
  mixins: [alertMixin, uiSettingsMixin, deviceMixins],
  props: {
    conversationId: {
      type: [Number, String],
      required: true,
    },
    inboxId: {
      type: Number,
      default: undefined,
    },
    onToggle: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      dragEnabled: true,
      conversationSidebarItems: [],
      dragging: false,
      showContactFormModal: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      contactsUiFlags: 'contacts/getUIFlags',
      uiFlags: 'inboxAssignableAgents/getUIFlags',
    }),
    contactDisplayName() {
      const c = this.contact || {};
      return c.name || c.email || '';
    },
    conversationAdditionalAttributes() {
      return this.currentConversationMetaData.additional_attributes || {};
    },
    channelType() {
      return this.currentChat.meta?.channel;
    },
    contact() {
      return this.$store.getters['contacts/getContact'](this.contactId);
    },
    contactAdditionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    contactId() {
      return this.currentChat.meta?.sender?.id;
    },
    currentConversationMetaData() {
      return this.$store.getters[
        'conversationMetadata/getConversationMetadata'
      ](this.conversationId);
    },
    hasContactAttributes() {
      const { custom_attributes: customAttributes } = this.contact;
      return customAttributes && Object.keys(customAttributes).length;
    },
  },
  watch: {
    conversationId(newConversationId, prevConversationId) {
      if (newConversationId && newConversationId !== prevConversationId) {
        this.getContactDetails();
      }
    },
    contactId() {
      this.getContactDetails();
      this.$store.dispatch('schoolEnrollments/get', this.contactId);
    },
  },
  mounted() {
    this.conversationSidebarItems = this.conversationSidebarItemsOrder;
    this.getContactDetails();
    this.$store.dispatch('attributes/get', 0);
  },
  methods: {
    onPanelToggle() {
      this.onToggle();
    },
    openContactFormModal() {
      this.showContactFormModal = true;
    },
    closeContactFormModal() {
      this.showContactFormModal = false;
    },
    getContactDetails() {
      if (this.contactId) {
        this.$store.dispatch('contacts/show', { id: this.contactId });
      }
    },
    getAttributesByModel() {
      if (this.contactId) {
        this.$store.dispatch('contacts/show', { id: this.contactId });
      }
    },
    openTranscriptModal() {
      this.showTranscriptModal = true;
    },

    onDragEnd() {
      this.dragging = false;
      this.updateUISettings({
        conversation_sidebar_items_order: this.conversationSidebarItems,
      });
    },

    async onSubmitContactInfo(contactItem) {
      await this.$store.dispatch('contacts/update', contactItem);
      await this.$store.dispatch(
        'contacts/fetchContactableInbox',
        this.contact.id
      );
    },
    onContactFormSuccess() {
      this.closeContactFormModal();
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .contact--profile {
    @apply pb-3 border-b border-solid border-slate-75 dark:border-slate-700;
  }
  .conversation--actions .multiselect-wrap--small {
    .multiselect {
      @apply box-border pl-6;
    }
    .multiselect__element {
      span {
        @apply w-full;
      }
    }
  }
}
</style>
